/**
 * Storage Settings Component - Configure local or cloud storage for dice registration
 *
 * Allows users to configure storage options:
 * - Cloud: Images stored on DiceCapture servers (default)
 * - Local Bridge: Direct HTTP upload to a bridge server on local network
 * - FTP: Upload via AWS Lambda proxy to user's FTP server (requires public IP)
 */

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { bridgeService } from '@/services/bridgeService';
import { ftpSettingsService, type FtpConfigInput } from '@/services/ftpSettingsService';
import { useSoulBondsStore } from '@/stores/soulBondsStore';

const SERVICE_TAG = '[STORAGE_SETTINGS]';

/** Extended storage mode including local bridge and download */
export type StorageMode = 'cloud' | 'bridge' | 'ftp' | 'both' | 'download';

interface StorageSettingsProps {
  deviceId: string;
  /** When true, renders without Card wrapper for use inside Collapsible */
  embedded?: boolean;
}

export function StorageSettings({ deviceId, embedded = false }: StorageSettingsProps) {
  const { appSettings, updateSettings } = useSoulBondsStore();
  const storageConfig = appSettings.storageConfig;

  // Local form state
  const [storageMode, setStorageMode] = useState<StorageMode>(storageConfig?.mode || 'cloud');

  // Bridge settings
  const [bridgeUrl, setBridgeUrl] = useState(storageConfig?.bridgeUrl || '');

  // FTP settings
  const [host, setHost] = useState('');
  const [port, setPort] = useState('21');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [basePath, setBasePath] = useState('/dicecapture');

  // UI state
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBridgeForm, setShowBridgeForm] = useState(storageMode === 'bridge');
  const [showFtpForm, setShowFtpForm] = useState(storageMode === 'ftp' || storageMode === 'both');

  // Load existing config on mount
  useEffect(() => {
    async function loadConfig() {
      if (!deviceId) return;

      // Load bridge config from local store
      if (storageConfig?.mode === 'bridge' && storageConfig?.bridgeUrl) {
        setBridgeUrl(storageConfig.bridgeUrl);
        setShowBridgeForm(true);
      }

      // Load FTP config from backend
      try {
        const status = await ftpSettingsService.getFtpStatus(deviceId);
        if (status.configured && (status.storageMode === 'ftp' || status.storageMode === 'both')) {
          setStorageMode(status.storageMode);
          setHost(status.host || '');
          setPort(String(status.port || 21));
          setBasePath(status.basePath || '/dicecapture');
          setShowFtpForm(true);
          setShowBridgeForm(false);

          // Update store
          updateSettings({
            storageConfig: {
              mode: status.storageMode,
              ftpConfigured: true,
              ftpHost: status.host,
              ftpPort: status.port,
              ftpBasePath: status.basePath,
              connectionStatus: status.connectionStatus,
              lastTestedAt: status.lastTestedAt
            }
          });
        }
      } catch (err) {
        console.warn(`${SERVICE_TAG} Could not load FTP status`, {
          error: err instanceof Error ? err.message : 'Unknown error'
        });
      }
    }

    loadConfig();
  }, [deviceId, updateSettings, storageConfig?.mode, storageConfig?.bridgeUrl]);

  const handleStorageModeChange = useCallback((mode: StorageMode) => {
    setStorageMode(mode);
    setShowBridgeForm(mode === 'bridge');
    setShowFtpForm(mode === 'ftp' || mode === 'both');
    setTestResult(null);
    setError(null);
  }, []);

  const buildFtpConfig = useCallback((): FtpConfigInput => ({
    host,
    port: parseInt(port, 10) || 21,
    username,
    password,
    basePath,
    useFtps: true
  }), [host, port, username, password, basePath]);

  const handleTestConnection = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);
    setError(null);

    try {
      let result: { success: boolean; message: string; latencyMs?: number };

      if (storageMode === 'bridge') {
        // Test bridge connection
        if (!bridgeUrl) {
          setError('Please enter the bridge server URL');
          setIsTesting(false);
          return;
        }
        result = await bridgeService.testConnection(bridgeUrl);
      } else {
        // Test FTP connection
        if (!host || !username || !password) {
          setError('Please fill in all required fields');
          setIsTesting(false);
          return;
        }
        result = await ftpSettingsService.testConnection(deviceId, buildFtpConfig());
      }

      setTestResult(result);

      if (result.success) {
        console.info(`${SERVICE_TAG} Connection test successful`, {
          mode: storageMode,
          latencyMs: result.latencyMs
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Connection test failed';
      setTestResult({ success: false, message });
      console.error(`${SERVICE_TAG} Connection test failed`, { error: message });
    } finally {
      setIsTesting(false);
    }
  }, [storageMode, bridgeUrl, deviceId, host, username, password, buildFtpConfig]);

  const handleSave = useCallback(async () => {
    // If cloud only, just update local settings
    if (storageMode === 'cloud') {
      updateSettings({
        storageConfig: {
          mode: 'cloud',
          ftpConfigured: false,
          bridgeUrl: undefined
        }
      });
      setTestResult({ success: true, message: 'Settings saved - using cloud storage only' });
      return;
    }

    // If download mode, save to local settings
    if (storageMode === 'download') {
      updateSettings({
        storageConfig: {
          mode: 'download',
          ftpConfigured: false,
          bridgeConfigured: false,
          bridgeUrl: undefined
        }
      });
      setTestResult({ success: true, message: 'Settings saved - images will download to your device' });
      console.info(`${SERVICE_TAG} Download mode configured`);
      return;
    }

    // If bridge mode, save locally (no backend needed)
    if (storageMode === 'bridge') {
      if (!bridgeUrl) {
        setError('Please enter the bridge server URL');
        return;
      }

      updateSettings({
        storageConfig: {
          mode: 'bridge',
          bridgeUrl,
          bridgeConfigured: true,
          ftpConfigured: false,
          connectionStatus: 'success',
          lastTestedAt: new Date().toISOString()
        }
      });
      setTestResult({ success: true, message: 'Bridge configuration saved successfully' });
      console.info(`${SERVICE_TAG} Bridge config saved`, { bridgeUrl });
      return;
    }

    // Validate FTP config
    if (!host || !username || !password) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await ftpSettingsService.saveFtpConfig(deviceId, buildFtpConfig(), storageMode);

      // Update local store
      updateSettings({
        storageConfig: {
          mode: storageMode,
          ftpConfigured: true,
          ftpHost: host,
          ftpPort: parseInt(port, 10) || 21,
          ftpBasePath: basePath,
          connectionStatus: 'success',
          lastTestedAt: new Date().toISOString()
        }
      });

      setTestResult({ success: true, message: 'FTP configuration saved successfully' });
      console.info(`${SERVICE_TAG} FTP config saved`, { storageMode });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save configuration';
      setError(message);
      console.error(`${SERVICE_TAG} Failed to save FTP config`, { error: message });
    } finally {
      setIsSaving(false);
    }
  }, [deviceId, storageMode, bridgeUrl, host, port, username, password, basePath, buildFtpConfig, updateSettings]);

  const handleClearConfig = useCallback(async () => {
    if (!window.confirm('Remove storage configuration? Images will only be saved to cloud.')) {
      return;
    }

    try {
      // Clear FTP config from backend if it was configured
      if (storageConfig?.ftpConfigured) {
        await ftpSettingsService.deleteFtpConfig(deviceId);
      }

      // Reset local state
      setStorageMode('cloud');
      setBridgeUrl('');
      setHost('');
      setUsername('');
      setPassword('');
      setBasePath('/dicecapture');
      setShowBridgeForm(false);
      setShowFtpForm(false);
      setTestResult(null);

      // Update store
      updateSettings({
        storageConfig: {
          mode: 'cloud',
          ftpConfigured: false,
          bridgeConfigured: false,
          bridgeUrl: undefined
        }
      });

      console.info(`${SERVICE_TAG} Storage config cleared`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear configuration');
    }
  }, [deviceId, storageConfig?.ftpConfigured, updateSettings]);

  const getStatusBadge = () => {
    if (storageConfig?.mode === 'download') {
      return <Badge variant="success">Download</Badge>;
    }
    if (storageConfig?.mode === 'bridge' && storageConfig?.bridgeConfigured) {
      return <Badge variant="success">Bridge</Badge>;
    }
    if (storageConfig?.ftpConfigured) {
      if (storageConfig.connectionStatus === 'success') {
        return <Badge variant="success">Connected</Badge>;
      }
      if (storageConfig.connectionStatus === 'failed') {
        return <Badge variant="destructive">Failed</Badge>;
      }
    }
    if (storageConfig?.mode === 'cloud') {
      return <Badge variant="secondary">Cloud</Badge>;
    }
    return <Badge variant="outline">Not Configured</Badge>;
  };

  const content = (
    <>
      {!embedded && (
        <div className="flex items-center justify-between mb-4">
          {getStatusBadge()}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/30">
          {error}
        </div>
      )}

      {testResult && (
        <div className={`mb-4 rounded-md p-3 text-sm border ${
          testResult.success
            ? 'bg-green-500/10 text-green-600 border-green-500/30'
            : 'bg-destructive/10 text-destructive border-destructive/30'
        }`}>
          {testResult.message}
        </div>
      )}

      {/* Storage Mode Selection */}
      <div className="space-y-3 mb-6">
        <label className="text-sm font-medium">Storage Mode</label>
        <div className="space-y-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="storageMode"
              value="cloud"
              checked={storageMode === 'cloud'}
              onChange={() => handleStorageModeChange('cloud')}
              className="h-4 w-4 text-primary"
            />
            <div>
              <span className="font-medium">Cloud Only</span>
              <span className="text-muted-foreground text-sm ml-2">(Default)</span>
              <p className="text-xs text-muted-foreground">Images stored on Dice Frog servers</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="storageMode"
              value="bridge"
              checked={storageMode === 'bridge'}
              onChange={() => handleStorageModeChange('bridge')}
              className="h-4 w-4 text-primary"
            />
            <div>
              <span className="font-medium">Local Bridge</span>
              <span className="text-muted-foreground text-sm ml-2">(Recommended for local training)</span>
              <p className="text-xs text-muted-foreground">Direct upload to PC on your local network</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="storageMode"
              value="ftp"
              checked={storageMode === 'ftp'}
              onChange={() => handleStorageModeChange('ftp')}
              className="h-4 w-4 text-primary"
            />
            <div>
              <span className="font-medium">Self-Hosted FTP</span>
              <p className="text-xs text-muted-foreground">Via AWS (requires public IP)</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="storageMode"
              value="both"
              checked={storageMode === 'both'}
              onChange={() => handleStorageModeChange('both')}
              className="h-4 w-4 text-primary"
            />
            <div>
              <span className="font-medium">Both Cloud + FTP</span>
              <p className="text-xs text-muted-foreground">Redundant storage for backup</p>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="storageMode"
              value="download"
              checked={storageMode === 'download'}
              onChange={() => handleStorageModeChange('download')}
              className="h-4 w-4 text-primary"
            />
            <div>
              <span className="font-medium">Download to Phone</span>
              <span className="text-muted-foreground text-sm ml-2">(Offline)</span>
              <p className="text-xs text-muted-foreground">Save images to your device, transfer manually</p>
            </div>
          </label>
        </div>
      </div>

      {/* Local Bridge Configuration Form */}
      {showBridgeForm && (
        <div className="space-y-4 pt-4 border-t">
          <h4 className="font-medium text-sm">Local Bridge Server</h4>
          <p className="text-xs text-muted-foreground">
            Run <code className="bg-muted px-1 rounded">python bridge_server.py</code> on your PC, then enter the URL shown.
          </p>

          <div className="space-y-2">
            <label htmlFor="bridgeUrl" className="text-sm text-muted-foreground">
              Bridge URL
            </label>
            <Input
              id="bridgeUrl"
              type="text"
              placeholder="http://192.168.1.100:8080"
              value={bridgeUrl}
              onChange={(e) => setBridgeUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Your phone and PC must be on the same WiFi network
            </p>
          </div>
        </div>
      )}

      {/* FTP Configuration Form */}
      {showFtpForm && (
        <div className="space-y-4 pt-4 border-t">
          <h4 className="font-medium text-sm">FTP Server Settings</h4>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-2">
              <label htmlFor="ftpHost" className="text-sm text-muted-foreground">
                Host
              </label>
              <Input
                id="ftpHost"
                type="text"
                placeholder="ftp.myserver.com"
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="ftpPort" className="text-sm text-muted-foreground">
                Port
              </label>
              <Input
                id="ftpPort"
                type="number"
                placeholder="21"
                value={port}
                onChange={(e) => setPort(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label htmlFor="ftpUsername" className="text-sm text-muted-foreground">
                Username
              </label>
              <Input
                id="ftpUsername"
                type="text"
                placeholder="dice_user"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="ftpPassword" className="text-sm text-muted-foreground">
                Password
              </label>
              <Input
                id="ftpPassword"
                type="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="ftpBasePath" className="text-sm text-muted-foreground">
              Base Path
            </label>
            <Input
              id="ftpBasePath"
              type="text"
              placeholder="/dicecapture"
              value={basePath}
              onChange={(e) => setBasePath(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Images will be saved to: {basePath}/{'{deviceId}'}/{'{diceType}'}/
            </p>
          </div>

          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              id="useFtps"
              checked={true}
              disabled
              className="h-4 w-4"
            />
            <label htmlFor="useFtps">
              Use FTPS (FTP over TLS) - Required for security
            </label>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        {(showBridgeForm || showFtpForm) && (
          <Button
            type="button"
            variant="outline"
            onClick={handleTestConnection}
            disabled={isTesting || (showBridgeForm && !bridgeUrl) || (showFtpForm && (!host || !username || !password))}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
        )}

        <Button
          type="button"
          variant="default"
          onClick={handleSave}
          disabled={isSaving || (showBridgeForm && !bridgeUrl) || (showFtpForm && (!host || !username || !password))}
          className="flex-1"
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>

        {(storageConfig?.ftpConfigured || storageConfig?.bridgeConfigured) && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleClearConfig}
            className="text-destructive hover:text-destructive"
          >
            Clear
          </Button>
        )}
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Storage Configuration</CardTitle>
            <CardDescription>
              Choose where dice registration images are saved
            </CardDescription>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
